import React, { Fragment } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Switch from '@material-ui/core/Switch';
import EditIcon from '@material-ui/icons/Edit';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import InfoIcon from '@material-ui/icons/Info';
import { PhaseTypeEnum } from '@vatos-pas/common';
import {
  CAN_APPROVE_PHASE,
  CAN_TOGGLE_GARAGE,
  CAN_OVERRIDE_CONTRACTOR,
  CAN_LOG_GARAGE,
} from 'providers/permissions';
import { useStyles } from '../../views/master-sheet-styles';
import { dateWithoutServerTimezone } from '../../utils/dateWithoutServerTimezone';
import ContractorChangeDialog from './ContractorChangeDialog';
import ContractorHistoryDialog from './ContractorHistoryDialog';
import { RequestResourceModal } from './RequestResourceModal';
import { PhotoUploadDialog } from './PhotoUploadDialog';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import {
  getPhaseDisplayName,
  BUMP_PHASES,
} from '../../constants/phaseConfigurations';
import { SKIPPED_PHASE_DISPLAY } from 'utils/phaseUtils';
import { useWorkflowConfig } from '../../config';
import {
  getResourceRequested,
  getResourceAllocated,
  getSupervisorApproved,
  getRequestedBy,
  getStartDate,
  getApprovedDate,
  showCameraPhase,
  isApproved,
  showPhaseImages,
} from './useTimelineLogic';
import { PhaseApprovedGeoIcon } from './PhaseApprovedGeoIcon';

interface DesktopTimelineViewProps {
  logic: any;
}

export const DesktopTimelineView: React.FC<DesktopTimelineViewProps> = ({
  logic,
}) => {
  const classes = useStyles();
  const config = useWorkflowConfig();

  const {
    selectedJob,
    refreshJobPhases,
    applyTimelinePayload,
    hasPermission,
    garageEnabled,
    openContractorModal,
    setOpenContractorModal,
    selectedJobPhaseForEdit,
    openHistoryModal,
    setOpenHistoryModal,
    selectedJobPhaseForHistory,
    openRequestResource,
    setOpenRequestResource,
    selectedJobPhaseForModal,
    setSelectedJobPhaseForModal,
    selectedPhaseType,
    setSelectedPhaseType,
    openPhotoUpload,
    setOpenPhotoUpload,
    selectedJobPhaseForPhotos,
    isViewPhotosMode,
    toggleGarage,
    setToggleGarage,
    openConfirmGoBack,
    setOpenConfirmGoBack,
    openInitialDate,
    setOpenInitialDate,
    openInitialFinishingDate,
    setOpenInitialFinishingDate,
    initialDate,
    initialFinishingDate,
    changedBySupervisor,
    changedBySupervisorFinishing,
    getPhase,
    phasesToRender,
    onChangeGarage,
    handleGoBack,
    goBackJob,
    handleRequestResource,
    handleLogGarage,
    handleApprove,
    handleEditContractor,
    handleViewPhotos,
    handleHistoryModal,
    needStrike,
    getNextStatusText,
    checkRequestResourcePermission,
    isRequestResourceDisabled,
    getRequestButtonLabel,
    isGoBackDisabled,
    isGarageDisabled,
    approveJob,
    geoLocationError,
    geoCoords,
    geoAccuracy,
    geoLoading,
    requestLocation,
    getNextStatusTextProp,
    goBackWarningMessage,
  } = logic;

  const isBumpoutActive =
    config.timeline.bumpoutTimesheetDesktopBehaviors &&
    selectedJob?.job?.reqBumpoutTimesheet;

  if (!selectedJob) {
    return <div>Loading...</div>;
  }

  const renderPhaseRow = (phaseType: PhaseTypeEnum) => {
    const phaseDisplayName = getPhaseDisplayName(phaseType);
    const isGaragePhase = phaseType === PhaseTypeEnum.Garage;
    const isBumpPhase = BUMP_PHASES.includes(phaseType);
    const isBumpoutBump = isBumpoutActive && isBumpPhase;
    const allocatedName = getResourceAllocated(
      phaseType,
      selectedJob,
      getPhase,
      isBumpoutBump,
    );
    const isSkipped = allocatedName === SKIPPED_PHASE_DISPLAY;
    const phaseApproved = isApproved(phaseType, selectedJob, getPhase);

    // Bumpout: strikethrough suppressed for bump phases only; history click guard for bumps
    const getNameClassName = () => {
      if (isSkipped) return undefined;
      if (isBumpoutBump) return undefined;
      return needStrike(phaseType) ? classes.striked : classes.normal;
    };

    const getNameOnClick = () => {
      if (isSkipped) return undefined;
      if (isBumpoutBump) return undefined;
      return () => handleHistoryModal(phaseType);
    };

    // Bumpout: inverted edit icon condition for bumps
    const showEditIcon =
      !isSkipped &&
      hasPermission(CAN_OVERRIDE_CONTRACTOR) &&
      allocatedName &&
      (isBumpoutBump ? !phaseApproved : phaseApproved);

    if (isGaragePhase && !garageEnabled) {
      return null;
    }

    return (
      <Box
        key={phaseType}
        display="flex"
        width="100%"
        mt={2}
        pb={2}
        className={classes.borderGrid}
      >
        <Box
          display="flex"
          width="12%"
          minWidth="12%"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Typography>{phaseDisplayName}</Typography>
          {isGaragePhase && hasPermission(CAN_TOGGLE_GARAGE) && (
            <Switch
              checked={toggleGarage}
              onChange={() =>
                onChangeGarage(
                  selectedJob,
                  toggleGarage,
                  refreshJobPhases,
                  setToggleGarage,
                  getPhase,
                )
              }
              disabled={isGarageDisabled()}
              color="primary"
              size="small"
              name="checkedB"
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
          )}
        </Box>
        <Box
          display="flex"
          width="12%"
          minWidth="12%"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Typography>
            {getRequestedBy(phaseType, selectedJob, getPhase)}
          </Typography>
        </Box>
        <Box
          display="flex"
          width="14%"
          minWidth="14%"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Typography>
            {getResourceRequested(phaseType, selectedJob, getPhase)}
          </Typography>
        </Box>
        <Box
          display="flex"
          width="15%"
          minWidth="15%"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Typography onClick={getNameOnClick()} className={getNameClassName()}>
            {allocatedName}
          </Typography>
          {showEditIcon && (
            <EditIcon
              onClick={() => handleEditContractor(phaseType)}
              className={classes.editIcon}
            />
          )}
        </Box>
        <Box
          display="flex"
          width="13%"
          minWidth="13%"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Typography>
            {getStartDate(phaseType, selectedJob, getPhase)}
          </Typography>
          {phaseType === PhaseTypeEnum.Finishing && initialFinishingDate && (
            <InfoIcon
              className={classes.info}
              onClick={() => setOpenInitialFinishingDate(true)}
            />
          )}
          {phaseType === PhaseTypeEnum.Spraying && initialDate && (
            <InfoIcon
              className={classes.info}
              onClick={() => setOpenInitialDate(true)}
            />
          )}
        </Box>
        <Box
          display="flex"
          width="13%"
          minWidth="13%"
          justifyContent="flex-start"
          alignItems="center"
          style={{ gap: 4 }}
        >
          <Typography>
            {getApprovedDate(phaseType, selectedJob, getPhase)}
          </Typography>
          <PhaseApprovedGeoIcon
            phaseType={phaseType}
            selectedJob={selectedJob}
          />
        </Box>
        <Box
          display="flex"
          width="13%"
          minWidth="13%"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Typography>
            {getSupervisorApproved(phaseType, selectedJob, getPhase)}
          </Typography>
        </Box>
        {/* Bumpout: hide approve/camera column for bump phases */}
        {!isBumpoutBump && (
          <Box
            display="flex"
            width="8%"
            minWidth="8%"
            alignItems="center"
            justifyContent="flex-start"
          >
            {showCameraPhase(phaseType, selectedJob, getPhase) &&
              hasPermission(CAN_APPROVE_PHASE) && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  className={classes.buttonTimeline}
                  onClick={() => handleApprove(phaseType)}
                >
                  Approve
                </Button>
              )}
            {!showCameraPhase(phaseType, selectedJob, getPhase) &&
              showPhaseImages(phaseType, selectedJob, getPhase) && (
                <CameraAltIcon
                  className={classes.pointer}
                  onClick={() => handleViewPhotos(phaseType)}
                />
              )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box px={3}>
      <Box className={classes.titleTabTimeline}>
        <Typography className={classes.titleFontBigger}>Timeline</Typography>
        <Box display="flex" alignItems="center">
          {geoLocationError && (
            <Typography className={classes.geoLocationError}>
              {geoLocationError}
            </Typography>
          )}
          <Box
            display="flex"
            alignItems="center"
            className={classes.backgroundButton}
            mb={1}
          >
            <Typography className={classes.titleFont}>
              {getNextStatusTextProp
                ? getNextStatusTextProp(selectedJob)
                : getNextStatusText(selectedJob)}
            </Typography>
            {checkRequestResourcePermission() && (
              <Fragment>
                <Button
                  variant="contained"
                  size="large"
                  className={classes.buttonTimeline}
                  onClick={handleGoBack}
                  disabled={isGoBackDisabled()}
                >
                  Go Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  className={classes.buttonTimeline}
                  onClick={() => {
                    if (
                      hasPermission(CAN_LOG_GARAGE) &&
                      selectedJob?.jobPhase?.priority === 200
                    ) {
                      handleLogGarage();
                    } else {
                      handleRequestResource();
                    }
                  }}
                  disabled={
                    !selectedJob ||
                    !checkRequestResourcePermission() ||
                    isRequestResourceDisabled()
                  }
                >
                  {getRequestButtonLabel()}
                </Button>
              </Fragment>
            )}
          </Box>
        </Box>
      </Box>

      <Box>
        <Box
          display="flex"
          width="100%"
          mt={2}
          pb={2}
          className={classes.borderGrid}
        >
          <Box
            display="flex"
            width="12%"
            minWidth="12%"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Typography>Phase</Typography>
          </Box>
          <Box
            display="flex"
            width="12%"
            minWidth="12%"
            alignItems="center"
            justifyContent="flex-start"
          >
            <Typography>Requested by</Typography>
          </Box>
          <Box
            display="flex"
            width="14%"
            minWidth="14%"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Typography>Resource Requested</Typography>
          </Box>
          <Box
            display="flex"
            width="15%"
            minWidth="15%"
            alignItems="center"
            justifyContent="flex-start"
          >
            <Typography>Name</Typography>
          </Box>
          <Box
            display="flex"
            width="13%"
            minWidth="13%"
            alignItems="center"
            justifyContent="flex-start"
          >
            <Typography>Start Date</Typography>
          </Box>
          <Box
            display="flex"
            width="13%"
            minWidth="13%"
            alignItems="center"
            justifyContent="flex-start"
          >
            <Typography>Approved Date</Typography>
          </Box>
          <Box
            display="flex"
            width="13%"
            minWidth="13%"
            alignItems="center"
            justifyContent="flex-start"
          >
            <Typography>Approved By</Typography>
          </Box>
          <Box
            display="flex"
            width="8%"
            minWidth="8%"
            alignItems="center"
            justifyContent="flex-start"
          ></Box>
        </Box>

        {phasesToRender.map(phaseType => renderPhaseRow(phaseType))}
      </Box>

      {selectedJobPhaseForEdit && (
        <ContractorChangeDialog
          regionId={selectedJob?.job?.regionId}
          handleClose={() => setOpenContractorModal(false)}
          open={openContractorModal}
          onTimelinePayload={applyTimelinePayload}
          selectedJobPhase={selectedJobPhaseForEdit}
          job={selectedJob}
        />
      )}

      {selectedJobPhaseForHistory && (
        <ContractorHistoryDialog
          handleClose={() => setOpenHistoryModal(false)}
          open={openHistoryModal}
          selectedJobPhase={selectedJobPhaseForHistory}
        />
      )}

      {selectedJobPhaseForModal && selectedPhaseType && (
        <RequestResourceModal
          open={openRequestResource}
          handleClose={() => {
            setOpenRequestResource(false);
            setSelectedJobPhaseForModal(null);
            setSelectedPhaseType(null);
          }}
          phaseType={selectedPhaseType}
          selectedJobPhase={selectedJobPhaseForModal}
          job={selectedJob}
          regionId={selectedJob?.job?.regionId}
          refreshJobPhases={refreshJobPhases}
          onTimelinePayload={applyTimelinePayload}
          geoCoords={geoCoords}
          geoAccuracy={geoAccuracy}
          geoLoading={geoLoading}
          requestLocation={requestLocation}
          geoError={geoLocationError}
        />
      )}

      {selectedJobPhaseForPhotos && (
        <PhotoUploadDialog
          open={openPhotoUpload}
          handleClose={() => setOpenPhotoUpload(false)}
          selectedPhase={selectedJobPhaseForPhotos}
          isViewPhotos={isViewPhotosMode}
          job={selectedJob}
          approveJob={isViewPhotosMode ? undefined : approveJob}
          geoCoords={geoCoords}
          geoAccuracy={geoAccuracy}
          geoLoading={geoLoading}
          requestLocation={requestLocation}
          geoError={geoLocationError}
        />
      )}

      <ConfirmDialog
        open={openConfirmGoBack}
        title={goBackWarningMessage}
        handleClose={() => setOpenConfirmGoBack(false)}
        onConfirm={goBackJob}
      />

      <ConfirmDialog
        open={openInitialDate}
        title=""
        content={
          <>
            <p>
              Initial Start date:{' '}
              {initialDate
                ? dateWithoutServerTimezone(initialDate).toLocaleDateString()
                : ''}
            </p>
            {changedBySupervisor && <p>Note: Supervisor adjusted date</p>}
          </>
        }
        handleClose={() => setOpenInitialDate(false)}
      />
      <ConfirmDialog
        open={openInitialFinishingDate}
        title=""
        content={
          <>
            <p>
              Initial Start date:{' '}
              {initialFinishingDate
                ? dateWithoutServerTimezone(
                    initialFinishingDate,
                  ).toLocaleDateString()
                : ''}
            </p>
            {changedBySupervisorFinishing && (
              <p>Note: Supervisor adjusted date</p>
            )}
          </>
        }
        handleClose={() => setOpenInitialFinishingDate(false)}
      />
    </Box>
  );
};

export default DesktopTimelineView;
